import React, { useRef, useEffect } from 'react';
import { Navigation, Autoplay } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { IoChevronBackOutline, IoChevronForward } from 'react-icons/io5';
import 'swiper/css';
import 'swiper/css/navigation';

//internal import
import BlogCard from '@component/blog/BlogCard';

const BlogCarousel = ({ posts }) => {
    const prevRef = useRef(null);
    const nextRef = useRef(null);
    const swiperRef = useRef(null);    // Function to equalize slide heights
    const equalizeSlideHeights = () => {
        if (swiperRef.current && swiperRef.current.slides) {
            const slides = swiperRef.current.slides;
            let maxHeight = 0;

            // Reset heights and find max height of card containers
            slides.forEach((slide) => {
                const cardContainer = slide.querySelector('a > div');
                if (cardContainer) {
                    cardContainer.style.height = 'auto';
                    slide.style.height = 'auto';
                    const slideHeight = slide.offsetHeight;
                    if (slideHeight > maxHeight) {
                        maxHeight = slideHeight;
                    }
                }
            });

            // Set all card containers to max height
            slides.forEach((slide) => {
                const cardContainer = slide.querySelector('a > div');
                if (cardContainer && maxHeight > 0) {
                    slide.style.height = `${maxHeight}px`;
                    cardContainer.style.height = '100%';
                }
            });
        }
    };

    useEffect(() => {
        // Equalize heights on component mount and window resize
        const handleResize = () => {
            setTimeout(equalizeSlideHeights, 100);
        };

        window.addEventListener('resize', handleResize);

        // Initial height equalization
        setTimeout(equalizeSlideHeights, 300);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [posts]);

    if (!posts || posts.length === 0) {
        return null;
    } return (
        <div className="relative px-4">
            {/* Navigation Buttons */}
            <button
                ref={prevRef}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-gray-50 transition-colors duration-200"
                aria-label="Previous blog posts"
            >
                <IoChevronBackOutline className="text-gray-600 w-6 h-6" />
            </button>

            <button
                ref={nextRef}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-gray-50 transition-colors duration-200"
                aria-label="Next blog posts"
            >
                <IoChevronForward className="text-gray-600 w-6 h-6" />
            </button><Swiper
                onSwiper={(swiper) => {
                    swiperRef.current = swiper;
                }}
                onInit={(swiper) => {
                    swiper.params.navigation.prevEl = prevRef.current;
                    swiper.params.navigation.nextEl = nextRef.current;
                    swiper.navigation.init();
                    swiper.navigation.update();

                    // Equalize heights after initialization
                    setTimeout(() => equalizeSlideHeights(), 100);
                }}
                onSlideChange={() => {
                    // Equalize heights after slide change
                    setTimeout(() => equalizeSlideHeights(), 50);
                }}
                spaceBetween={20}
                navigation={{
                    prevEl: prevRef.current,
                    nextEl: nextRef.current,
                }}
                allowTouchMove={true}
                loop={posts.length > 3}
                autoplay={{
                    delay: 5000,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: true,
                }}
                breakpoints={{
                    320: {
                        slidesPerView: 1,
                        spaceBetween: 16,
                    },
                    640: {
                        slidesPerView: 2,
                        spaceBetween: 20,
                    },
                    1024: {
                        slidesPerView: 3,
                        spaceBetween: 24,
                    },
                    1280: {
                        slidesPerView: 4,
                        spaceBetween: 24,
                    },
                }}
                modules={[Navigation, Autoplay]}
                className="blog-carousel"
            >
                {posts.map((post) => (
                    <SwiperSlide key={post.id} className="!h-auto">
                        <BlogCard post={post} />
                    </SwiperSlide>
                ))}
            </Swiper>
        </div>
    );
};

export default BlogCarousel;
